import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { format, toZonedTime } from 'date-fns-tz'
import { Formik } from 'formik'
import Select from 'react-select'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { useGetBuildingPlaces } from '@/api/clients/get-building-places'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { NumericInput } from '@/components/ui/numeric-input'
import { popupFactory, toastFactory } from '@/factories'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import api from '../../../api/api'
import ModalContainer from '../../ModalContainer/ModelContainer'
import Icon from '../../Icons'
import { capitalize } from '../../../helpers/helpers'
import { HouseholdMember } from '../VisitCreateModal/HouseholdMember'
import Loader from '../../Loader/Loader'
import {
  getAgeStringTranslated,
  useMemoizedPoolSelectOptions,
  shouldDisplayPoolInfo,
} from '../helpers'

import { displayUserFullName, getValidationSchema } from './helpers'

export const VisitDetailsModal = ({ guestPassVisit, onClose, timezone, fetchGuestPassVisits }) => {
  const { t } = useTranslation(['clients-legacy', 'common'])
  const client = guestPassVisit.clientId
  const [logs, setLogs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isFetchingClients, setIsFetchingClients] = useState(false)
  const [clients, setClients] = useState([])

  const selectedAppBuilding = useSelector(selectActiveBuilding)
  const selectedBuilingId = selectedAppBuilding?.id

  const { data: places, isFetching: isPlacesFetching } = useGetBuildingPlaces(selectedBuilingId)

  const isPoolInfoAvailable = shouldDisplayPoolInfo(client.buildingId)
  const poolSelectOptions = useMemoizedPoolSelectOptions(places)
  const validationSchema = useMemo(() => getValidationSchema(t), [t])

  const initialValues = {
    householdMembersIds:
      guestPassVisit?.householdMembers?.map((householdMember) => householdMember.clientId.id) || [],
    guestCount: guestPassVisit?.guestCount || 0,
    ...(isPoolInfoAvailable && {
      pool: guestPassVisit?.placeId?.id
        ? poolSelectOptions.find((option) => option.value === guestPassVisit.placeId.id)
        : poolSelectOptions[0],
    }),
  }

  const fetchClients = async () => {
    try {
      setIsFetchingClients(true)
      const response = await api.getGuestPassDetailsByClientId(guestPassVisit.userCode)
      const { clients = [] } = response.data
      setClients(clients)
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setIsFetchingClients(false)
    }
  }

  const fetchLogs = async () => {
    setIsLoading(true)
    try {
      const response = await api.getGuestPassVisitsLogs(guestPassVisit.id)
      setLogs(response.data)
    } catch (error) {
      console.error('Error fetching guest pass visits logs:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveGuestPassVisit = async (values, setSubmitting, resetForm) => {
    try {
      const guestCount = parseInt(values.guestCount)

      const data = {
        ...values,
        // Historical household members no longer eligible (moved out, deactivated) aren't
        // rendered as checkboxes, so drop any that survived from the visit's original snapshot
        // instead of sending an id the user never had a chance to see or uncheck.
        householdMembersIds: values.householdMembersIds.filter((id) =>
          clients.some((eligibleClient) => eligibleClient.id === id),
        ),
        ...(isPoolInfoAvailable && {
          placeId: values.pool.value,
        }),
        guestCount: isNaN(guestCount) ? 0 : guestCount,
      }

      delete data.pool

      await api.updateGuestPassVisit(guestPassVisit.id, data)
      setSubmitting(true)
      await Promise.all([fetchGuestPassVisits(true), fetchLogs()])
      resetForm(values)
      // Note: the popup only receives `guestPassVisit` once, at open time (popupFactory.open
      // stores props once and never refreshes them). Without this, the read-only "Ex-guests" row
      // and a subsequent re-open of edit mode would still show stale pre-save values.
      popupFactory.update(VisitDetailsModal, {
        guestPassVisit: {
          ...guestPassVisit,
          guestCount: data.guestCount,
          ...(isPoolInfoAvailable && {
            placeId: places.find((place) => place.id === data.placeId) ?? guestPassVisit.placeId,
          }),
        },
      })
      toastFactory.success(t('clients-legacy:profileView.visits.saveSuccess'))
    } catch (error) {
      console.error('Error saving guest pass visit:', error)
      toastFactory.error(t('clients-legacy:profileView.visits.saveError'))
    } finally {
      setSubmitting(false)
      setIsEditing(false)
    }
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    await saveGuestPassVisit(values, setSubmitting, resetForm)
  }

  useEffect(() => {
    fetchLogs()
  }, [guestPassVisit.id])

  useEffect(() => {
    if (isEditing && client.id) {
      fetchClients()
    }
  }, [client.id, isEditing])

  return (
    <ModalContainer
      additionalClass="visit-details-modal-container"
      onClose={onClose}
      headerTitle={t('clients-legacy:profileView.visits.detailsHeaderTitle')}
      headerIcon={<Icon icon="users" />}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values,
          errors,
          handleChange,
          isSubmitting,
          isValid,
          dirty,
          resetForm,
        }) => {
          const { householdMembersIds = [], guestCount = 0, pool } = values

          const handleHouseholdMemberChange = ({ id, checked }) => {
            if (checked) {
              handleChange({
                target: {
                  name: 'householdMembersIds',
                  value: [...householdMembersIds, id],
                },
              })
              return
            }

            handleChange({
              target: {
                name: 'householdMembersIds',
                value: householdMembersIds.filter((memberId) => memberId !== id),
              },
            })
          }

          const handleGuestCountChange = (value) => {
            handleChange({ target: { name: 'guestCount', value } })
          }

          const handleIncrement = () => {
            handleChange({ target: { name: 'guestCount', value: Number(guestCount) + 1 } })
          }

          const handleDecrement = () => {
            if (Number(guestCount) > 0) {
              handleChange({ target: { name: 'guestCount', value: Number(guestCount) - 1 } })
            }
          }

          const handlePoolChange = (value) => {
            handleChange({
              target: {
                name: 'pool',
                value,
              },
            })
          }

          return (
            <div className={isLoading ? 'is-loading' : ''}>
              <div className="visit-details-container">
                <div className="visit-details-header">
                  {t('clients-legacy:visitCreate.passInformation')}
                </div>
                {isEditing && isPoolInfoAvailable && (
                  <Select
                    className="select-container"
                    classNamePrefix="select-primary"
                    name="pool"
                    options={poolSelectOptions}
                    onChange={handlePoolChange}
                    value={pool}
                    selectStyle={{ width: '100%' }}
                    isDisabled={isPlacesFetching}
                  />
                )}
                <div className="visit-details-table-wrapper">
                  {!isEditing && isPoolInfoAvailable && (
                    <div className="visit-row-container">
                      <div>{t('clients-legacy:profileView.visits.amenityLabel')}</div>
                      <div>{guestPassVisit.placeId?.name}</div>
                    </div>
                  )}
                  <div className="visit-row-container">
                    <div>{t('clients-legacy:visitCreate.residentLabel')}</div>
                    <div>{displayUserFullName(client.user)}</div>
                  </div>
                  <div className="visit-row-container">
                    <div>{t('clients-legacy:table.columns.phoneNumber')}</div>
                    <div>{formatPhoneNumberDisplay(client.phone)}</div>
                  </div>
                  <div className="visit-row-container">
                    <div>{t('clients-legacy:newClientForm.addressPlaceholder')}</div>
                    <div>{client.street}</div>
                  </div>
                  <div className="visit-row-container">
                    <div>{t('clients-legacy:visitCreate.ageLabel')}</div>
                    <div>{getAgeStringTranslated(client.birth, t)}</div>
                  </div>
                  <div className="visit-row-container">
                    <div>{t('clients-legacy:profileView.visits.dateLabel')}</div>
                    <div>
                      {format(
                        toZonedTime(guestPassVisit.createdAt, timezone),
                        'MM/dd/yyyy hh:mm a',
                      )}
                    </div>
                  </div>
                </div>
                <div className="visit-details-header">
                  {t('clients-legacy:profileView.visits.guestListTitle')}
                </div>
                {!isEditing && (
                  <div className="visit-details-table-wrapper">
                    {guestPassVisit.householdMembers.map((householdMember) => (
                      <div className="visit-row-container" key={householdMember.id}>
                        <div>{t('clients-legacy:profileView.visits.householdMember')}</div>
                        <div className="visit-guest-name-container">
                          <span
                            className="visit-guest-name"
                            title={displayUserFullName(householdMember.clientId.user)}
                          >
                            {displayUserFullName(householdMember.clientId.user)}
                          </span>
                          {getAgeStringTranslated(householdMember.clientId.birth, t)}
                        </div>
                      </div>
                    ))}
                    <div className="visit-row-container">
                      <div>{t('clients-legacy:profileView.visits.exGuests')}</div>
                      <div>{guestPassVisit.guestCount}</div>
                    </div>
                  </div>
                )}
                {isEditing && (
                  <>
                    {isFetchingClients ? (
                      <div>
                        <Loader />
                      </div>
                    ) : (
                      <>
                        {clients.length > 0 ? (
                          <>
                            <div
                              className={`visit-details-table-wrapper ${
                                isFetchingClients ? 'is-loading' : ''
                              }`}
                            >
                              {clients.map((client) => (
                                <HouseholdMember
                                  clientId={client.id}
                                  isChecked={householdMembersIds.includes(client.id)}
                                  isLoading={isFetchingClients}
                                  key={client.id}
                                  user={client.user}
                                  userBirth={client.birth}
                                  onChange={handleHouseholdMemberChange}
                                />
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="visit-details-header">
                            {t('clients-legacy:profileView.visits.guestPassNotFound')}
                          </div>
                        )}
                      </>
                    )}
                    <div className="visit-details-header">
                      {t('clients-legacy:visitCreate.guestPassesToRedeemLabel')}
                    </div>
                    <Flex gap={8} align="center">
                      <NumericInput
                        size="small"
                        min={0}
                        value={String(guestCount)}
                        onChange={handleGuestCountChange}
                      />
                      <Button
                        use="secondary"
                        size="small"
                        onlyBox
                        type="button"
                        onClick={handleDecrement}
                      >
                        -
                      </Button>
                      <Button
                        use="secondary"
                        size="small"
                        onlyBox
                        type="button"
                        onClick={handleIncrement}
                      >
                        +
                      </Button>
                    </Flex>
                    {errors.guestCount ? (
                      <label className="help is-danger">{errors.guestCount}</label>
                    ) : null}
                  </>
                )}
                <div className="visit-details-header">
                  {t('clients-legacy:profileView.visits.activityLog')}
                </div>
                <div className="visit-details-table-wrapper">
                  {logs.map((log) => (
                    <div className="visit-row-container" key={log.id}>
                      <div>
                        {t('clients-legacy:profileView.visits.checkInAction', {
                          action: capitalize(log.action),
                        })}
                      </div>
                      <div className="visit-row-value--stacked">
                        <div>{displayUserFullName(log.initiator)}</div>
                        <div>
                          {format(toZonedTime(log.createdAt, timezone), 'hh:mm a MM/dd/yyyy')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`visit-edit-actions ${isEditing ? 'edit-mode' : ''}`}>
                {isEditing ? (
                  <>
                    <button
                      className="button button-gray"
                      type="button"
                      onClick={() => {
                        setIsEditing(false)
                        resetForm()
                      }}
                    >
                      {t('common:actions.cancel')}
                    </button>
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={isSubmitting || !isValid || !dirty}
                      onClick={handleSubmit}
                    >
                      {t('common:actions.save')}
                    </button>
                  </>
                ) : (
                  <button className="button button-transparent" onClick={() => setIsEditing(true)}>
                    {t('common:actions.edit')}
                  </button>
                )}
              </div>
            </div>
          )
        }}
      </Formik>
    </ModalContainer>
  )
}

VisitDetailsModal.propTypes = {
  guestPassVisit: PropTypes.shape({
    clients: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    createdAt: PropTypes.number,
    guestCount: PropTypes.number,
    id: PropTypes.number,
    userCode: PropTypes.string,
  }),
  timezone: PropTypes.string,
  onClose: PropTypes.func,
}
